import { Grid } from '@mui/material';
import { HSpacer, RoutalAvatar, VSpacer } from '@nx-smartmonkey/ui';

import { OrganizationUser } from '../../../../domain/organization/OrganizationUser';
import { User } from '../../../../domain/user/User';
import Text from '../../../../react-components/Text';
import { DataTable } from '../../../../react-components/data-table/DataTable';
import { getUploadAsBase64 } from '../../../../services/requests';

type NewProjectUsersProps = {
  loggedUser: User;
  selectedUsers: string[];
  setSelectedUsers: (users: string[]) => void;
  organizationUsers: OrganizationUser[];
};

export const NewProjectUsers = ({
  loggedUser,
  selectedUsers,
  setSelectedUsers,
  organizationUsers,
}: NewProjectUsersProps) => {
  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      justifyContent="center"
      style={{ maxWidth: `1000px`, padding: `0 20px`, height: `100%` }}
    >
      <Text variant="h6" intlId="projects.create.users" />

      <VSpacer />

      <div style={{ height: `100%`, maxHeight: `calc(100% - 240px)` }}>
        <DataTable
          noFooter
          docs={organizationUsers.map((organizationUser: OrganizationUser) => {
            return {
              doc: organizationUser,
              checkboxIcon: { element: <div style={{ width: `24px` }} /> },
            };
          })}
          total={organizationUsers.length}
          selectedIds={selectedUsers.includes(loggedUser.id) ? selectedUsers : [...selectedUsers, loggedUser.id]}
          onMultipleSelection={({ selectedIds, fromGlobalCheckbox }) => {
            if (fromGlobalCheckbox) {
              if (selectedUsers.length > 0) {
                setSelectedUsers([]);
              } else {
                setSelectedUsers((organizationUsers ?? []).map((user) => user.id));
              }
            } else {
              const users = organizationUsers.filter((organizationUser) =>
                (selectedIds ?? []).includes(organizationUser.id)
              );
              setSelectedUsers((users ?? []).map((user) => user.id));
            }
          }}
          columns={[
            {
              width: `calc(100% - 220px)`,
              accessorName: `name`,
              name: <Text variant="body" intlId="settings.organization.users.name" />,
              formatter: ({ doc: user }: { doc: OrganizationUser }) => (
                <Grid container direction="row" wrap="nowrap" justifyContent="center" alignItems="center">
                  <Grid item>
                    <RoutalAvatar
                      url={user.avatar_url}
                      getUploadAsBase64={getUploadAsBase64}
                      label={`${user.first_name}_${user.last_name}`}
                      labelStyles={{ fontSize: `12px`, lineHeight: 2 }}
                      containerStyles={{ width: `32px`, height: `32px` }}
                    />
                  </Grid>

                  <HSpacer medium />

                  <Grid item container direction="column">
                    <Grid item>
                      <Text variant="body-large">{`${user.first_name} ${user.last_name}`}</Text>
                    </Grid>
                    <Grid item>
                      <Text variant="label">{user.email}</Text>
                    </Grid>
                  </Grid>
                </Grid>
              ),
            },
            {
              align: `center`,
              accessorName: `role`,
              name: (
                <Text
                  variant="body"
                  style={{ textTransform: `none` }}
                  intlId="settings.organization.users.organization_role"
                />
              ),
              formatter: ({ doc: organizationUser }: { doc: OrganizationUser }) => {
                let role = ``;
                switch (organizationUser.role) {
                  case `organization_administrator`:
                    role = `administrator`;
                    break;
                  case `organization_manager`:
                    role = `manager`;
                    break;
                  case `organization_user`:
                    role = `user`;
                    break;
                }

                return <Text variant="body-large" intlId={`settings.organization.users.roles.${role}`} />;
              },
            },
          ]}
        />
      </div>
    </Grid>
  );
};
